// Utilitaire pour générer et gérer l'historique des modifications de version

// Fonction pour générer un historique des versions à partir des mises à jour du client
export const generateVersionHistory = (client) => {
  if (!client || !client.updates || !client.servers) {
    return [];
  }
  
  // Historique initial vide
  const history = [];
  
  // Ordonner les mises à jour par date (les plus anciennes d'abord)
  const sortedUpdates = [...client.updates].sort((a, b) => {
    return new Date(a.date) - new Date(b.date);
  });
  
  // Pour chaque serveur, suivre l'évolution des versions
  client.servers.forEach((server, serverIndex) => {
    const serverHistory = {
      serverIndex,
      serverName: server.name,
      serverType: server.type || 'Serveur tout-en-un',
      color: server.color || '#3B82F6',
      components: [
        { 
          name: 'MariaDB', 
          versions: [],
          property: 'mariadbVersion'
        },
        { 
          name: 'Apache', 
          versions: [],
          property: 'apacheVersion'
        },
        { 
          name: 'NodeJS', 
          versions: [],
          property: 'nodejsVersion'
        },
        { 
          name: 'Patient App', 
          versions: [],
          property: 'gitPatientAppVersion'
        },
        { 
          name: 'Manager Front App', 
          versions: [],
          property: 'gitManagerFrontAppVersion'
        },
        { 
          name: 'Marshaller', 
          versions: [],
          property: 'gitMarshallerVersion'
        },
        { 
          name: 'Marshaller Interop', 
          versions: [],
          property: 'gitMarshallerInteropVersion'
        }
      ]
    };
    
    // État initial (première version connue)
    const initialState = {
      date: client.createdAt,
      updateTitle: 'État initial',
      updateType: 'Initial',
      updateId: 'initial',
      versions: {
        mariadbVersion: server.mariadbVersion,
        apacheVersion: server.apacheVersion,
        nodejsVersion: server.nodejsVersion,
        gitPatientAppVersion: server.gitPatientAppVersion,
        gitManagerFrontAppVersion: server.gitManagerFrontAppVersion,
        gitMarshallerVersion: server.gitMarshallerVersion,
        gitMarshallerInteropVersion: server.gitMarshallerInteropVersion
      }
    };
    
    // Ajouter l'état initial pour chaque composant
    serverHistory.components.forEach(component => {
      const initialVersion = initialState.versions[component.property];
      if (initialVersion) {
        component.versions.push({
          version: initialVersion,
          date: initialState.date,
          updateTitle: initialState.updateTitle,
          updateType: initialState.updateType,
          updateId: initialState.updateId
        });
      }
    });
    
    // Reconstituer l'historique à partir des mises à jour
    const serverVersions = { ...initialState.versions };
    
    sortedUpdates.forEach(update => {
      // Vérifier si la mise à jour modifie des versions
      let hasChanges = false;
      
      // Vérifier les commits pour les applications
      if (update.commits) {
        if (update.commits.manager && serverVersions.gitManagerFrontAppVersion !== update.commits.manager) {
          serverVersions.gitManagerFrontAppVersion = update.commits.manager;
          hasChanges = true;
        }
        
        if (update.commits.marshallerFrontal && serverVersions.gitMarshallerVersion !== update.commits.marshallerFrontal) {
          serverVersions.gitMarshallerVersion = update.commits.marshallerFrontal;
          hasChanges = true;
        }
        
        if (update.commits.marshallerInterop && serverVersions.gitMarshallerInteropVersion !== update.commits.marshallerInterop) {
          serverVersions.gitMarshallerInteropVersion = update.commits.marshallerInterop;
          hasChanges = true;
        }
        
        if (update.commits.patientApp && serverVersions.gitPatientAppVersion !== update.commits.patientApp) {
          serverVersions.gitPatientAppVersion = update.commits.patientApp;
          hasChanges = true;
        }
      }
      
      // Si des versions ont changé, ajouter les nouvelles versions à l'historique
      if (hasChanges) {
        serverHistory.components.forEach(component => {
          const currentVersion = serverVersions[component.property];
          if (currentVersion) {
            // Vérifier si la version a changé par rapport à la dernière version enregistrée
            const lastVersion = component.versions.length > 0 ? 
              component.versions[component.versions.length - 1].version : null;
            
            if (lastVersion !== currentVersion) {
              component.versions.push({
                version: currentVersion,
                date: update.date,
                updateTitle: update.title,
                updateType: update.updateType || 'Production',
                updateId: update.id
              });
            }
          }
        });
      }
    });
    
    // Ajouter l'historique du serveur à l'historique global
    history.push(serverHistory);
  });
  
  return history;
};

// Fonction pour créer un tableau de toutes les versions de composants pour un serveur
export const flattenServerVersionHistory = (serverHistory) => {
  if (!serverHistory || !serverHistory.components) {
    return [];
  }
  
  const flatHistory = [];
  
  serverHistory.components.forEach(component => {
    component.versions.forEach(versionInfo => {
      flatHistory.push({
        component: component.name,
        property: component.property,
        ...versionInfo
      });
    });
  });
  
  // Trier par date
  return flatHistory.sort((a, b) => new Date(b.date) - new Date(a.date));
};
